import { FamilyRelationshipRole, PersonName, PersonSet, ViewPerson } from "./ResearchDataModel"
import { useAssertionStore } from './AssertionStore'
import { useResearchDataApi } from './ResearchDataApi'
import { AsyncData, LoadMode } from "@webapp/util/AsyncData"
import { AsyncDataStore, VersionedPartialKeyList } from "@webapp/util/AsyncDataStore"
import { defineStore } from "pinia"
import { DateTime } from "luxon"
import { DateTimeUtil } from "@webapp/util/LuxonUtil"
import { flatten, groupBy, identity, uniq, uniqBy } from "lodash"
import { assignExisting, isDefined } from "@webapp/util/TypeScriptUtil"
import { ViewChild, ViewGraph } from './ViewGraph'
import { AncestorPathItem, RelationPath } from './RelationPath'
import { plainToPersonDisplayProperties, usePersonStore } from './PersonStore'
import { useViewFamilyStore } from './ViewFamilyStore'
import { some } from '@webapp/util/IterableUtil'
import { processResponsiveAsync } from '@webapp/util/AppUtil'
import { useDataGroupStore } from '@webapp/gp/DataGroupStore'

const maxGenerations = 100 // prevent infinite loops
const viewPersonExpand = "displayProperties,matches/displayProperties"
export const viewGraphExpand = `persons/displayProperties,persons/matches/displayProperties,families/displayProperties` // don't expand father/mother

export const useViewPersonStore = defineStore("viewPersonStore", () => {
    const asyncViewPersons = new Map<string, AsyncData<ViewPerson>>()
    const asyncSpouseFamilyKeys = new Map<string, AsyncData<VersionedPartialKeyList>>()
    const asyncParentFamilyKeys = new Map<string, AsyncData<VersionedPartialKeyList>>()
    const asyncPersonSets = new Map<string, AsyncData<PersonSet[]>>()
    const commonAncestors = new Map<string, Map<string, string | undefined>>()

    const rd = useResearchDataApi()
    const personStore = usePersonStore()
    const viewFamilyStore = useViewFamilyStore()
    const assertionStore = useAssertionStore()
    // const dataGroupStore = useDataGroupStore()

    // watch(() => dataGroupStore.getAsyncKeyListForVisible().data?.keys, (newKeys, oldKeys) => {
    //     const newSet = new Set(newKeys)
    //     const oldSet = new Set(oldKeys)
    //     const added = newKeys?.filter(id => !oldSet.has(id)).length ?? 0
    //     const removed = oldKeys?.filter(id => !newSet.has(id)).length ?? 0
    //     if (added || removed) {
    //         console.log(`ViewPersonStore: visible groups changed (+${added}/-${removed}), clearing cache`)
    //         asyncViewPersons.clear()
    //         asyncSpouseFamilyKeys.clear()
    //         asyncParentFamilyKeys.clear()
    //         commonAncestors.clear()
    //     }
    // }, { deep: true })

    function getAsyncPerson(id: string | undefined, loadMode = LoadMode.TrackOnly) {
        return id ? getAsyncPersonList([id], loadMode)[0] : undefined
    }

    function getAsyncPersonList(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const store = useViewPersonStore() // use to get/return reactive store objects
        const loadPersonsAsync = async (idsToLoad: string[], requestTime: DateTime) => {
            const plainItems = await rd.getPlainByIdsAsync("viewpersons", idsToLoad, viewPersonExpand)
            return processExpandedViewPersons(plainItems, requestTime)
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncViewPersons, ids, "ViewPerson", loadMode, loadPersonsAsync)
    }

    async function getPersonListAsync(ids: (string | undefined)[]) {
        const asyncItems = getAsyncPersonList(ids, LoadMode.EnsureLoaded)
        return (await Promise.all(asyncItems.map(a => a.whenLoadCompleted))).filter(isDefined)        
    }

    async function searchByNamesAsync(names: string[]) {
        const requestTime = DateTime.utc()
        const expand = rd.injectExpandItems(viewPersonExpand)
        const plainItems = await rd.getPlainAsync("viewpersons", { q: `name:"${names.join(',')}"`, expand }) as any[]
        addViewPersonsToStore(plainItems, requestTime)

        // return reactive async proxies from store map
        const store = useViewPersonStore() // use to get/return reactive store objects
        return plainItems.map(p => store.asyncViewPersons.get(p.id!)!)
    }

    function getLoadedPerson(id: string | undefined) {
        const store = useViewPersonStore() // use to get/return reactive store objects
        return store.asyncViewPersons.get(id ?? '')?.data
    }

    function getLoadedPersonList(ids: (string | undefined)[]) {
        return getAsyncPersonList(ids).map(a => a.data).filter(isDefined)
    }

    function areMatchesLoaded(personIds: (string | undefined)[]) {
        const asyncPersons = getAsyncPersonList(personIds.filter(isDefined))
        if (asyncPersons.some(a => !a.isLoadComplete()))
            return false
        const matchIds = asyncPersons.flatMap(a => a.data?.matchIds ?? [])
        return AsyncDataStore.areItemsLoaded(personStore.asyncPersons, matchIds)
    }

    function areDetailsLoaded(personId: string | undefined) {
        const store = useViewPersonStore() // use to get/return reactive store objects
        const asyncVP = store.asyncViewPersons.get(personId ?? '')
        if (!asyncVP || !asyncVP.isLoadComplete())
            return false

        // check matches loaded
        const matchIds = asyncVP.data?.matchIds ?? []
        if (!AsyncDataStore.areItemsLoaded(personStore.asyncPersons, matchIds))
            return false

        // check assertion key lists loaded
        const asyncAssertionKeyLists = AsyncDataStore.getAsyncItemsIfAllLoaded(personStore.asyncAssertionKeys, matchIds)
        if (!asyncAssertionKeyLists)
            return false

        // check assertions loaded
        const assertionIds = asyncAssertionKeyLists.flatMap(akl => akl.data?.keys ?? [])
        return assertionStore.areExpandedAssertionsLoaded(assertionIds)
    }

    async function ensureDetailsLoadedAsync(personId: string) {
        if (areDetailsLoaded(personId)) {
            console.log(`Details for ${personId} already loaded`)
            return
        }
        const expand = "displayProperties,matches/displayProperties,assertions/place,assertions/site,contentTags/content/dataSignedUrls"
        const requestTime = DateTime.utc()
        const plainViewPersons = await rd.getPlainByIdsAsync("viewpersons", [personId], expand)

        processExpandedViewPersons(plainViewPersons, requestTime)
        processViewPersonAssertions(plainViewPersons, requestTime)
        processViewPersonContent(plainViewPersons, requestTime)
    }

    function hasLoadedAncestors(personId: string) {
        return some(getLoadedAncestors(personId, 1))
    }

    /**
     * Enumerates the loaded ancestors of the specified person, optionally including ancestors' other spouses and descendants.
     * The result MAY include duplicates.
     */
    function *getLoadedAncestors(startPersonId: string | undefined, generations: number, downGenerations = 0): Iterable<ViewPerson> {
        if (startPersonId && generations > 0) {
            const store = useViewPersonStore() // use to get/return reactive store objects
            const parentFams = getLoadedParentFamilies(startPersonId).parentFamilies
            const parentIds = uniq(parentFams.flatMap(pf => pf.spouseIds))
            for (const parentId of parentIds) {
                const parent = store.asyncViewPersons.get(parentId)!.data
                if (parent) {
                    yield parent
                }
            }
            if (downGenerations > 0) {
                const parentSpouseFams = uniq(parentIds.flatMap(id => viewFamilyStore.getLoadedSpouseFamilies(id).spouseFamilies))
                for (const parentSpouseFam of parentSpouseFams) {
                    for (const descendant of viewFamilyStore.getLoadedDescendants(parentSpouseFam.id!, downGenerations)) {
                        if (descendant.id != startPersonId) { // don't return the same person on the way down
                            yield descendant
                        }
                    }
                }
            }
            for (const parentId of parentIds) {
                for (const ancestor of getLoadedAncestors(parentId, generations - 1, downGenerations)) {
                    yield ancestor
                }
            }
        }
    }

    function areAncestorsLoaded(startPersonId: string, generations: number, downGenerations = 0): boolean {
        if (generations > 0) {
            const store = useViewPersonStore() // use to get/return reactive store objects
            const startPersonName = () => store.asyncViewPersons.get(startPersonId)?.data?.displayName ?? "unknown"

            // are parent family relationships loaded?
            const asyncParentFamilyKeyList = store.asyncParentFamilyKeys.get(startPersonId)
            if (!asyncParentFamilyKeyList || asyncParentFamilyKeyList.shouldLoad()) {
                console.log(`areAncestorsLoaded: Parent family IDs of ${startPersonName()} (${startPersonId}) not loaded`)
                return false
            }
            if (!asyncParentFamilyKeyList.data) {
                return true // can't load
            }
            if (!asyncParentFamilyKeyList.data.isComplete) {
                console.log(`areAncestorsLoaded: Parent family IDs of ${startPersonName()} (${startPersonId}) not complete`)
                return false
            }

            // are parent families loaded?
            const parentFamilyIds = asyncParentFamilyKeyList.data.keys
            const asyncParentFamilies = parentFamilyIds.map(id => viewFamilyStore.asyncViewFamilies.get(id))
            if (asyncParentFamilies.some(a => !a || a.shouldLoad())) {
                console.log(`areAncestorsLoaded: One or more of ${parentFamilyIds.length} parent families of ${startPersonName()} (${startPersonId}) not loaded`)
                return false
            }
            const asyncParentFamiliesCanLoad = asyncParentFamilies.filter(isDefined).filter(a => a.data)
            
            // are parents loaded?
            const parentIds = uniq(flatten(asyncParentFamiliesCanLoad.map(a => a.data!.spouseIds)))

            const asyncParents = parentIds.map(id => store.asyncViewPersons.get(id))
            if (asyncParents.some(a => !a || a.shouldLoad())) {
                console.log(`areAncestorsLoaded: One or more of ${parentIds.length} parents of ${startPersonName()} (${startPersonId}) not loaded`)
                return false
            }

            // are parent descendants (spouses, children) loaded?
            if (parentIds.some(id => !areDescendantsLoaded(id, downGenerations))) {
                return false
            }

            if (generations > 1) {
                // are other requested ancestors loaded? (recursive)
                return parentIds.map(id => areAncestorsLoaded(id, generations - 1, downGenerations)).every(identity)
            }
        }
        return true // all loaded
    }

    async function ensureAncestorsLoadedAsync(personIds: (string | undefined)[], generations: number, downGenerations = 0, reload = false) {
        const idsToLoad = []
        for (const id of personIds.filter(isDefined)) {
            if (!reload && areAncestorsLoaded(id, generations, downGenerations)) {
                console.log(`Ancestors of ${id} already loaded, will not reload`)
            }
            else {
                idsToLoad.push(id)
            }
        }
        if (idsToLoad.length == 0)
            return

        const requestTime = DateTime.utc()
        const pathId = idsToLoad.length == 1 ? idsToLoad[0] : '$list'
        const idsArg = idsToLoad.length > 1 ? idsToLoad.join(',') : undefined
        const path = `viewpersons/${pathId}/ancestors`
        const generationsArg = `${generations}d${downGenerations}`
        const graph = <ViewGraph> await rd.getPlainAsync(path, { generations: generationsArg, ids: idsArg, expand: viewGraphExpand })

        const personIdText = idsToLoad.length == 1 ? idsToLoad[0] : `${idsToLoad.length} persons`
        console.log(`Loaded ${graph.persons.length} ancestors (${generations} generations) of ${personIdText}: ${graph.persons.slice(0, 20).map(p => PersonName.getDisplayName(p.displayProperties.name))}`)
        await addViewGraphToStoresAsync(graph, requestTime, reload)

        for (const personId of idsToLoad) {
            if (!areAncestorsLoaded(personId, generations, downGenerations)) {
                console.log(`ensureAncestorsLoaded: Ancestors of ${personId} still not loaded after loading`)
            }
        }
    }

    function getAsyncSpouseFamiliesKeyList(personId: string | undefined, loadMode = LoadMode.TrackOnly) {
        const store = useViewPersonStore() // use to get/return reactive store objects
        const asyncKeyList = AsyncDataStore.getOrAddAsyncItems(store.asyncSpouseFamilyKeys, [personId], "ViewPerson.SpouseFamilyKeys").at(0)
        if (asyncKeyList && asyncKeyList.shouldLoadPartial(loadMode)) {
            const requestTime = DateTime.utc()                
            const path = `viewpersons/${personId}/spouses`
            const loadAsync = rd.getPlainAsync(path, { expand: viewGraphExpand }).then(async data => {
                const graph = <ViewGraph> data
                console.log(`Loaded ${graph.families.length} spouse families of person ${personId}`)
                await addViewGraphToStoresAsync(graph, requestTime, loadMode == LoadMode.Reload)
                return {
                    keys: graph.families.map(f => f.id),
                    modifiedDate: requestTime,
                    isComplete: true
                } as VersionedPartialKeyList
            })
            AsyncData.trackLoad(asyncKeyList, requestTime, loadAsync)
        }
        return asyncKeyList
    }

    function getAsyncParentFamiliesKeyList(personId: string | undefined) {
        // TODO: support LoadMode parameter and implement loading (for now must be loaded via ensureAncestorsLoadedAsync)
        const store = useViewPersonStore() // use to get/return reactive store objects
        return AsyncDataStore.getOrAddAsyncItems(store.asyncParentFamilyKeys, [personId], "ViewPerson.ParentFamilyKeys").at(0)
    }

    function hasLoadedDescendants(personId: string) {
        for (const vp of getLoadedDescendants(personId, 1)) {
            if (vp.id != personId) {
                return true
            }
        }
        return false
    }

    function *getLoadedDescendants(personId: string, generations: number): Iterable<ViewPerson> {
        const store = useViewPersonStore() // use to get/return reactive store objects
        const spouseFamilyIds = store.asyncSpouseFamilyKeys.get(personId)?.data?.keys ?? []
        for (const spouseFamilyId of spouseFamilyIds) {
            for (const descendant of viewFamilyStore.getLoadedDescendants(spouseFamilyId, generations)) {
                yield descendant
            }
        }
    }

    function areDescendantsLoaded(startPersonId: string, generations: number): boolean {
        if (generations > 0) {
            const store = useViewPersonStore() // use to get/return reactive store objects
            const startPersonName = store.asyncViewPersons.get(startPersonId)?.data?.displayName ?? "unknown"

            // are spouse family relationships loaded?
            const asyncSpouseFamilyKeyList = store.asyncSpouseFamilyKeys.get(startPersonId)
            if (!asyncSpouseFamilyKeyList || asyncSpouseFamilyKeyList.shouldLoad()) {
                console.log(`areDescendantsLoaded: Spouse family IDs of ${startPersonName} (${startPersonId}) not loaded`)
                return false
            }
            if (!asyncSpouseFamilyKeyList.data) {
                return true // can't load
            }
            if (!asyncSpouseFamilyKeyList.data?.isComplete) {
                console.log(`areDescendantsLoaded: Spouse family IDs of ${startPersonName} (${startPersonId}) not complete`)
                return false                    
            }

            // are spouse families loaded?
            const spouseFamilyIds = asyncSpouseFamilyKeyList!.data!.keys
            const asyncSpouseFamilies = spouseFamilyIds.map(id => viewFamilyStore.asyncViewFamilies.get(id))
            if (asyncSpouseFamilies.some(a => !a || a.shouldLoad())) {
                console.log(`areDescendantsLoaded: One or more spouse families of ${startPersonName} (${startPersonId})not loaded`)
                return false
            }
            const asyncSpouseFamiliesCanLoad = asyncSpouseFamilies.filter(isDefined).filter(a => a.data)

            // are spouses loaded?
            const spouseIds = uniq(asyncSpouseFamiliesCanLoad.map(a => a.data!.spouseOf(startPersonId)).filter(isDefined))
            const asyncSpouses = spouseIds.map(id => store.asyncViewPersons.get(id))
            if (asyncSpouses.some(a => !a || a.shouldLoad())) {
                console.log(`areDescendantsLoaded: One or more spouses of ${startPersonName} (${startPersonId}) not loaded`)
                return false
            }

            // are child relationships loaded?
            const asyncChildKeyLists = spouseFamilyIds.map(id => viewFamilyStore.asyncChildKeys.get(id))
            if (asyncChildKeyLists.some(a => !a || a.shouldLoad() || !a.data?.isComplete)) {
                console.log(`areDescendantsLoaded: Child IDs of one or more spouse families of ${startPersonName} (${startPersonId}) not loaded`)
                return false
            }
            
            // are children loaded?
            const childIds = uniq(flatten(asyncChildKeyLists.filter(isDefined).map(a => a.data?.keys ?? [])))
            const asyncChildren = childIds.map(id => store.asyncViewPersons.get(id))
            if (asyncChildren.some(a => !a || a.shouldLoad())) {
                console.log(`areDescendantsLoaded: One or more children of ${startPersonName} (${startPersonId}) not loaded`)
                return false
            }
            const asyncChildrenCanLoad = asyncChildren.filter(isDefined).filter(a => a.data)

            if (generations > 1) {
                // are child spouses and other requested descendants loaded? (recursive)
                return asyncChildrenCanLoad.every(a => areDescendantsLoaded(a.key, generations - 1))
            }
        }
        return true // all loaded
    }

    async function ensureDescendantsLoadedAsync(personId: string | undefined, generations: number, reload = false) {
        if (!personId || (!reload && areDescendantsLoaded(personId, generations))) {
            console.log(`Descendants of ${personId} already loaded, will not reload`)
            return
        }
        const requestTime = DateTime.utc()
        const path = `viewpersons/${personId}/descendants`
        const graph = <ViewGraph> await rd.getPlainAsync(path, { generations, expand: viewGraphExpand })
        console.log(`Loaded ${graph.persons.length} descendants (${generations} generations) of person ${personId}: ${graph.persons.slice(0, 20).map(p => PersonName.getDisplayName(p.displayProperties.name))}`)
        await addViewGraphToStoresAsync(graph, requestTime, reload)

        if (!areDescendantsLoaded(personId, generations)) {
            console.log(`ensureDescendantsLoaded: Descendants of ${personId} still not loaded after loading`)
        }
    }

    /**
     * Gets the common ancestor of startPersonId and toPersonId, if it is known and loaded. Uses cached common ancestor if available.
     */
    function getLoadedCommonAncestor(startPersonId: string, toPersonId: string): { personId?: string } | undefined {
        const store = useViewPersonStore() // use to get/return reactive store objects
        let personCommonAncestors = store.commonAncestors.get(startPersonId)
        let ancestorId = personCommonAncestors?.get(toPersonId)
        if (!ancestorId) {
            // not cached, so try to find relation path using previously loaded data
            const commonAncestor = findLoadedCommonAncestor(startPersonId, toPersonId)
            if (!commonAncestor) {
                console.log(`Failed to find relation path from ${startPersonId} to ${toPersonId}, but some data not loaded so not sure`)
                return undefined // not found, but may still exist
            }

            ancestorId = commonAncestor.personId // undefined if no common ancestor exists

            // add result to cache
            if (!personCommonAncestors) {
                personCommonAncestors = new Map<string, string | undefined>()
                const store = useViewPersonStore() // use to get/return reactive store objects
                store.commonAncestors.set(startPersonId, personCommonAncestors)
            }
            personCommonAncestors.set(toPersonId, ancestorId)
        }
        return { personId: ancestorId }
    }

    async function ensureRelationPathsLoadedAsync(startPersonId: string, toPersonIds: string[], reload = false) {
        const needPathIds = toPersonIds.filter(id => reload || !getLoadedRelationPath(startPersonId, id))
        if (needPathIds.length > 0) {
            const requestTime = DateTime.utc()
            const path = `viewpersons/${startPersonId}/relationpaths`
            const graph = <ViewGraph> await rd.getPlainAsync(path, { to: toPersonIds, expand: viewGraphExpand })
            console.log(`Loaded relation path from ${startPersonId} to ${toPersonIds.length} persons (returned ${graph.persons.length} persons)`)
            await addViewGraphToStoresAsync(graph, requestTime, reload)
        }
    }

    /**
     * Gets the relation path from startPersonId to toPersonId, if it is loaded. Uses cached common ancestor if available.
     */
    function getLoadedRelationPath(startPersonId: string, toPersonId: string): RelationPath | undefined {
        if (startPersonId === toPersonId) {
            return {
                startPersonPath: [],
                commonAncestorId: startPersonId,
                toPersonPath: [],
            }
        }
        const store = useViewPersonStore() // use to get/return reactive store objects
        let startPersonCommonAncestors = store.commonAncestors.get(startPersonId)
        let commonAncestorId = startPersonCommonAncestors?.get(toPersonId)
        if (!commonAncestorId) {
            // not cached, so try to find relation path using previously loaded data
            const commonAncestor = findLoadedCommonAncestor(startPersonId, toPersonId)
            if (!commonAncestor) {
                console.log(`Failed to find relation path from ${startPersonId} to ${toPersonId}, but some data not loaded so not sure`)
                return undefined // no relation path found, but could still exist
            }
            if (!commonAncestor.personId) {
                return undefined // no relation path (definitive)
            }

            // found common ancestor
            commonAncestorId = commonAncestor.personId

            // add to common ancestors cache
            if (!startPersonCommonAncestors) {
                startPersonCommonAncestors = new Map<string, string | undefined>()
                store.commonAncestors.set(startPersonId, startPersonCommonAncestors)
            }
            startPersonCommonAncestors.set(toPersonId, commonAncestorId)
        }

        // get shortest paths to common ancestor
        const pathFromStartPersonToCommon = getLoadedPathToAncestor(startPersonId, commonAncestorId, maxGenerations)
        const pathFromToPersonToCommon = getLoadedPathToAncestor(toPersonId, commonAncestorId, maxGenerations)
        if (!pathFromStartPersonToCommon || !pathFromToPersonToCommon) {
            throw `Failed to get paths to common ancestor ${commonAncestorId}`
        }

        return {
            startPersonPath: pathFromStartPersonToCommon,
            commonAncestorId,
            toPersonPath: pathFromToPersonToCommon
        }
    }

    /**
     * Gets a shortest loaded path from personId to ancestorId, if one exists.
     */
    function getLoadedPathToAncestor(personId: string, ancestorId: string, maxGenerations: number): AncestorPathItem[] | undefined {
        if (personId === ancestorId) {
            return []
        }
        const loadedParentFamilies = getLoadedParentFamilies(personId)
        if (loadedParentFamilies.parentFamilies.length === 0) {
            return undefined // no path found
        }
        const familyWithAncestor = loadedParentFamilies.parentFamilies.find(f => f.hasSpouse(ancestorId))
        if (familyWithAncestor) {
            return [{ 
                ancestorId: personId, 
                parentFamilyId: familyWithAncestor.id!, 
            }] // found path
        }
        if (maxGenerations > 0) {
            // try to find path from parents to ancestor
            const parentRoles = [FamilyRelationshipRole.Father, FamilyRelationshipRole.Mother]
            const parentsChecked = new Set<string>()
            for (const parentFamily of loadedParentFamilies.parentFamilies) {
                for (const role of parentRoles) {
                    const parentId = parentFamily.getSpouseId(role)
                    if (parentId && !parentsChecked.has(parentId)) {
                        const parentPath = getLoadedPathToAncestor(parentId, ancestorId, maxGenerations - 1)
                        if (parentPath) {
                            return [{ 
                                ancestorId: personId, 
                                parentFamilyId: parentFamily.id!,
                            },
                            ...parentPath]
                        }
                        parentsChecked.add(parentId)
                    }
                }
            }
        }
        return undefined // no path found
    }

    /**
     * Traverse the ancestors of startPersonId and toPersonId, looking for an ancestor in common. If found, returns
     * an object containing the common ancestor's id. If not found and all ancestors were loaded, returns an object
     * with common ancestor id set to undefined (conclusive result that no common ancestor exists). If not found and 
     * not all parents were loaded, returns undefined (inconclusive result).
     */
    function findLoadedCommonAncestor(startPersonId: string, toPersonId: string): { personId?: string } | undefined {
        const startPersonAncestorIds = new Set<string>()
        const toPersonAncestorIds = new Set<string>()

        const startPersonGenIds = new Set<string>([startPersonId])
        const toPersonGenIds = new Set<string>([toPersonId])

        let allParentIdsComplete = true

        for (let i = 0; i < maxGenerations; i++) {
            
            // compare new stuff <-> new stuff and new stuff <-> old stuff, but not old stuff <-> old stuff
            const commonAncestorId = [...startPersonGenIds].filter(id => toPersonGenIds.has(id))
                .concat([...startPersonGenIds].filter(id => toPersonAncestorIds.has(id)))
                .concat([...toPersonGenIds].filter(id => startPersonAncestorIds.has(id)))
                .at(0)

            if (commonAncestorId) {
                return { personId: commonAncestorId } // found common ancestor
            }

            // add new stuff to old stuff
            startPersonGenIds.forEach(id => startPersonAncestorIds.add(id))
            toPersonGenIds.forEach(id => toPersonAncestorIds.add(id))

            // get new stuff
            const nextStartPersonGenIds = new Set<string>()
            for (const id of startPersonGenIds) {
                const loadedParentIds = getLoadedParentIds(id)
                if (!loadedParentIds.isComplete) {
                    allParentIdsComplete = false
                }
                loadedParentIds.parentIds.forEach(id => nextStartPersonGenIds.add(id))
            }
            const nextToPersonGenIds = new Set<string>()
            for (const id of toPersonGenIds) {
                const loadedParentIds = getLoadedParentIds(id)
                if (!loadedParentIds.isComplete) {
                    allParentIdsComplete = false
                }
                loadedParentIds.parentIds.forEach(id => nextToPersonGenIds.add(id))
            }

            if (nextStartPersonGenIds.size === 0 && nextToPersonGenIds.size === 0) {
                break // no more ancestors to check
            }

            startPersonGenIds.clear()
            nextStartPersonGenIds.forEach(id => startPersonGenIds.add(id))

            toPersonGenIds.clear()
            nextToPersonGenIds.forEach(id => toPersonGenIds.add(id))
        }

        if (allParentIdsComplete) {
            return {} // we checked and there is no common ancestor
        }

        return undefined // not found, but not all parent ids loaded, so not sure
    }

    /**
     * Gets all loaded parent families of the specified person.
     */
    function getLoadedSpouseFamilies(personId: string | undefined) {
        const store = useViewPersonStore() // use to get/return reactive store objects
        const vp = store.asyncViewPersons.get(personId ?? '')?.data // map match to view person if necessary
        const spouseFamilyKeyList = store.asyncSpouseFamilyKeys.get(vp?.id ?? '')?.data
        if (!spouseFamilyKeyList) {
            return { spouseFamilies: [], isComplete: false } // spouse family ids not loaded
        }
        const spouseFamilies = spouseFamilyKeyList.keys.map(id => viewFamilyStore.asyncViewFamilies.get(id)?.data)
        const loadedSpouseFamilies = spouseFamilies.filter(isDefined)
        const isComplete =  spouseFamilyKeyList.isComplete && spouseFamilies.every(isDefined)

        return { spouseFamilies: loadedSpouseFamilies, isComplete }
    }

    /**
     * Gets all loaded parent families of the specified person.
     */
    function getLoadedParentFamilies(personId: string | undefined) {
        const store = useViewPersonStore() // use to get/return reactive store objects
        const vp = store.asyncViewPersons.get(personId ?? '')?.data // map match to view person if necessary
        const parentFamilyKeyList = store.asyncParentFamilyKeys.get(vp?.id ?? '')?.data
        if (!parentFamilyKeyList) {
            return { parentFamilies: [], isComplete: false } // parent family ids not loaded
        }
        const parentFamilies = parentFamilyKeyList.keys.map(id => viewFamilyStore.asyncViewFamilies.get(id)?.data)
        const loadedParentFamilies = parentFamilies.filter(isDefined)
        const isComplete =  parentFamilyKeyList.isComplete && parentFamilies.every(isDefined)

        return { parentFamilies: loadedParentFamilies, isComplete }
    }

    /**
     * Gets the ids of all parents in the loaded parent families of the specified person.
     */
    function getLoadedParentIds(personId: string | undefined) {
        const loadedParentFamilies = getLoadedParentFamilies(personId)

        return { 
            parentIds: [...new Set(loadedParentFamilies.parentFamilies.map(f => f.spouseIds).flat())], 
            isComplete: loadedParentFamilies.isComplete
        }
    }

    async function definePersonSetsAsync(sets: string[][], groupId: string) {
        await rd.postPlainAsync(`groups/${groupId}/personsets/define`, { personSets: sets })

        const store = useViewPersonStore()
        sets.forEach(set => set.forEach(id => store.invalidate(id)))
    }

    async function extendPersonSetAsync(minimalSets: string[][], groupId: string) {
        await rd.postPlainAsync(`groups/${groupId}/personsets/extend`, minimalSets)

        const store = useViewPersonStore()
        minimalSets.forEach(set => set.forEach(id => store.invalidate(id)))
    }

    function getAsyncPersonSets(personId: string | undefined, loadMode = LoadMode.TrackOnly) {
        const store = useViewPersonStore()
        const loadAsync = async (idsToLoad: string[], requestTime: DateTime) => {
            const results = new Map<string, PersonSet[]>()
            for (const id of idsToLoad) {
                const plainSets = (await rd.getPlainAsync(`viewpersons/${id}/personsets`)) as any[]
                results.set(id, plainSets.map(s => plainToPersonSet(s)))
            }
            return results
        }
        return AsyncDataStore.getAsyncItemsByIds(store.asyncPersonSets, [personId], "PersonSets", loadMode, loadAsync)[0]
    }

    function getLoadedPersonSets(personId: string | undefined) {
        const store = useViewPersonStore()
        return store.asyncPersonSets.get(personId ?? '')?.data ?? []
    }

    async function getPersonSetsAsync(personId: string | undefined) {
        if (!personId)
            return []
        const asyncData = getAsyncPersonSets(personId, LoadMode.EnsureLoaded)
        return await asyncData?.whenLoadCompleted ?? []
    }

    function invalidate(personId: string | undefined) {
        // extra work to do here because we add an entry to the store for each match
        const store = useViewPersonStore() // use to get/return reactive store objects
        const viewFamilyStore = useViewFamilyStore()
        store.asyncViewPersons.get(personId ?? '')?.data?.matchIds.forEach(id => {
            AsyncData.invalidate(store.asyncViewPersons.get(id))
            AsyncData.invalidate(store.asyncPersonSets.get(id))
            AsyncData.invalidate(personStore.asyncPersons.get(id))
        })

        // view family matches are derived from spouse matches, so invalidate spouse families too
        const asyncFamilyKeys = store.asyncSpouseFamilyKeys.get(personId ?? '')
        asyncFamilyKeys?.data?.keys?.forEach(id => viewFamilyStore.invalidate(id))
        AsyncData.invalidate(asyncFamilyKeys)
    }

    function addViewPersonsToStore(plainViewPersons: any[], requestTime: DateTime) {
        if (plainViewPersons.some(p => !p.matches))
            throw new Error('All view persons added to store should have matches')
        if (plainViewPersons.some(p => !p.displayProperties))
            throw new Error('All view persons added to store should have display properties')

        const store = useViewPersonStore() // use to get/return reactive store objects
        const matchMap = processExpandedViewPersons(uniqBy(plainViewPersons, 'id'), requestTime)
        AsyncDataStore.addItemMapToStore(store.asyncViewPersons, "ViewPerson", matchMap, requestTime)
    }

    function processExpandedViewPersons(plainViewPersons: any[], requestTime: DateTime) {
        personStore.processExpandedPersonDisplayProperties(plainViewPersons.map(p => p.displayProperties), requestTime)
        
        // CONSIDER: only add non-placeholder matches to person store, to reduce store size. Some code may expect all
        // matches to be present, though, or force them to load for other reasons, though, so benefit may be minimal.
        const plainMatches = flatten(plainViewPersons.map(p => p.matches as any[]).filter(isDefined))
        personStore.addPersonsToStore(plainMatches, requestTime)

        const viewPersons = plainViewPersons.map(plainToViewPerson)

        // add an entry for EVERY match to the store (all returning the same view person)
        return new Map(flatten(viewPersons.map(vp => vp.matchIds.map(id => [id, vp]))))
    }

    /**
     * Adds assertion key lists and assertions for each view person match to the store. Assertion lists are assumed 
     * to be complete.
     */
    function processViewPersonAssertions(plainViewPersons: any[], requestTime: DateTime) {
        // group all assertions by subject (person) id
        const allAssertions = plainViewPersons.flatMap(p => p.assertions as any[] ?? [])
        const plainAssertionMap = new Map<string, any[]>(Object.entries(groupBy(allAssertions, a => a.subjectId as string)))

        // ensure there is an entry for every person because even empty lists are complete
        for (const personId of plainViewPersons.flatMap(vp => vp.matchIds as string[])) {
            if (!plainAssertionMap.has(personId)) {
                plainAssertionMap.set(personId, []) // no assertions for this person
            }
        }

        personStore.processExpandedPersonAssertions(plainAssertionMap, requestTime)
    }

    function processViewPersonContent(plainViewPersons: any[], requestTime: DateTime) {
        const allTags = plainViewPersons.flatMap(p => p.contentTags as any[] ?? [])
        const plainTagMap = new Map<string, any[]>(Object.entries(groupBy(allTags, t => t.itemId as string)))

        // ensure there is an entry for every person because even empty lists are complete
        for (const personId of plainViewPersons.flatMap(vp => vp.matchIds as string[])) {
            if (!plainTagMap.has(personId)) {
                plainTagMap.set(personId, []) // no content tags for this person
            }
        }

        personStore.processExpandedPersonContentTags(plainTagMap, requestTime)
    }

    async function addViewGraphToStoresAsync(graph: ViewGraph, requestTime: DateTime, reload: boolean) {
        const store = useViewPersonStore() // use to get/return reactive store objects

        const start = performance.now()

        // use process responsive because adding large #'s of objects to reactive stores can lock up the UI
        await processResponsiveAsync(graph.persons, batch => addViewPersonsToStore(batch, requestTime))
        await processResponsiveAsync(graph.families, batch => viewFamilyStore.addViewFamiliesToStore(batch, requestTime))

        const spouseRoles = [FamilyRelationshipRole.Father, FamilyRelationshipRole.Mother]
        const childrenByFamily = new Map<string, ViewChild[]>()

        await processResponsiveAsync(graph.persons, batch => {
            for (const gvp of batch) {
                const spouseRels = gvp.familyRelationships.filter(r => spouseRoles.includes(r.role))
                const spouseFamilyKeys = spouseRels.map(r => r.familyId)

                if (!reload || gvp.spouseFamiliesComplete) {
                    AsyncDataStore.addPartialKeyListToStore(store.asyncSpouseFamilyKeys, gvp.id, 
                        "ViewPerson.SpouseFamilyKeys", spouseFamilyKeys, !!gvp.spouseFamiliesComplete, requestTime, reload)
                }

                const childRels = gvp.familyRelationships.filter(r => r.role == FamilyRelationshipRole.Child)
                const parentFamilyKeys = childRels.map(r => r.familyId)

                if (!reload || gvp.parentFamiliesComplete) {
                    AsyncDataStore.addPartialKeyListToStore(store.asyncParentFamilyKeys, gvp.id, 
                        "ViewPerson.ParentFamilyKeys", parentFamilyKeys, !!gvp.parentFamiliesComplete, requestTime, reload)
                }

                for (const childRel of childRels) {
                    let children = childrenByFamily.get(childRel.familyId)
                    if (!children) {
                        children = []
                        childrenByFamily.set(childRel.familyId, children)
                    }
                    children.push({ id: gvp.id, childOrder: childRel.childOrder })
                }
            }
        })

        await processResponsiveAsync(graph.families, batch => {
            for (const gvf of batch) {
                if (!reload || gvf.childrenComplete) {
                    const children = childrenByFamily.get(gvf.id) ?? []
                    children.sort((a, b) => (a.childOrder ?? 0) - (b.childOrder ?? 0))
                    const childKeys = children.map(c => c.id)
                    AsyncDataStore.addPartialKeyListToStore(viewFamilyStore.asyncChildKeys, gvf.id, 
                        "ViewFamily.ChildKeys", childKeys, true, requestTime, reload)
                }
            }
        })

        console.log(`AddViewGraphToStores: Added ${graph.persons.length} persons and ${graph.families.length} families in ${(performance.now() - start).toFixed()} ms`)
    }

    return {
        asyncViewPersons,
        asyncSpouseFamilyKeys,
        asyncParentFamilyKeys,
        asyncPersonSets,
        commonAncestors,

        getAsyncPerson,
        getAsyncPersonList,
        getPersonListAsync,
        searchByNamesAsync,
        getLoadedPerson,
        getLoadedPersonList,
        areMatchesLoaded,
        areDetailsLoaded,
        ensureDetailsLoadedAsync,
        areAncestorsLoaded,
        ensureAncestorsLoadedAsync,
        hasLoadedAncestors,
        getLoadedAncestors,
        areDescendantsLoaded,
        ensureDescendantsLoadedAsync,
        hasLoadedDescendants,
        getLoadedDescendants,
        getAsyncSpouseFamiliesKeyList,
        getAsyncParentFamiliesKeyList,
        getLoadedCommonAncestor,
        ensureRelationPathsLoadedAsync,
        getLoadedRelationPath,
        findLoadedCommonAncestor,
        getLoadedPathToAncestor,
        getLoadedSpouseFamilies,
        getLoadedParentFamilies,
        getLoadedParentIds,
        definePersonSetsAsync,
        extendPersonSetAsync,
        getAsyncPersonSets,
        getLoadedPersonSets,
        getPersonSetsAsync,
        invalidate,

        addViewPersonsToStore,
        addViewGraphToStoresAsync,
    }
})

function plainToViewPerson(p: any) {
    const viewPerson = assignExisting(new ViewPerson(), p)
    viewPerson.displayProperties = plainToPersonDisplayProperties(p.displayProperties)
    return viewPerson
}

function plainToPersonSet(p: any) {
    const set = assignExisting(new PersonSet(), p)
    set.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate)
    return set
}
